import { Bitmap, OUTLINE, INK, shade, type RGB } from "./bitmap";

export type HairStyle = "short" | "long" | "bun" | "cap" | "spiky" | "bald";

export interface Look {
  hair: RGB;
  style: HairStyle;
  skin: RGB;
  shirt: RGB;
  pants: RGB;
  glasses: boolean;
  cap: RGB;
}

const HAIRS: RGB[] = [[82, 54, 38], [36, 30, 40], [60, 40, 30], [196, 120, 60], [150, 60, 50], [240, 200, 90], [110, 70, 44], [196, 196, 206], [226, 120, 60], [50, 34, 30]];
const STYLES: HairStyle[] = ["short", "long", "bun", "cap", "spiky", "short", "long", "bald"];
const SKINS: RGB[] = [[238, 198, 162], [204, 146, 108], [150, 98, 70], [228, 178, 140], [176, 120, 88], [120, 78, 56]];
const SHIRTS: RGB[] = [[222, 170, 64], [90, 140, 210], [180, 80, 120], [80, 170, 110], [60, 120, 160], [230, 90, 110], [230, 120, 90], [52, 150, 150], [196, 56, 64], [120, 72, 150], [236, 236, 230], [240, 150, 70]];
const PANTS: RGB[] = [[58, 62, 100], [70, 60, 50], [40, 44, 60], [96, 80, 70]];
const CAPS: RGB[] = [[60, 100, 200], [200, 60, 60], [60, 150, 90]];

export const LOOK_COUNT = 64;

/** Look number n (0..63) as a deterministic mix of hair, skin, clothes and glasses. */
export function look(n: number): Look {
  const i = ((n % LOOK_COUNT) + LOOK_COUNT) % LOOK_COUNT;
  return {
    hair: HAIRS[(i * 7 + 3) % HAIRS.length],
    style: STYLES[(i * 5 + 1) % STYLES.length],
    skin: SKINS[(i * 11 + 2) % SKINS.length],
    shirt: SHIRTS[(i * 3) % SHIRTS.length],
    pants: PANTS[(i * 13) % PANTS.length],
    glasses: i % 5 === 2,
    cap: CAPS[i % CAPS.length],
  };
}

// ------------------------------------------------------------------ 12 px studio sprites (from hd2d_iso.py)

const BACK = ["....oooo....", "...ohhhho...", "..ohhhhhho..", "..ohhhhhHo..", ".oshhhhhHso.", "..ohhhhHHo..", "...oossoo...", "..otttttTo..", ".otttttttTo.", "otttttttttTo", "otttttttttTo", "ottttttttTTo", ".oooooooooo."];
const FRONT = ["....oooo....", "...ohhhho...", "..ohhhhhho..", "..ohhhhhHo..", "..ohssssHo..", "..osesseso..", "..osssssSo..", "...osssSo...", "..oottttoo..", ".ottttttTTo.", ".ottttttTTo.", ".osttttttso.", "..oppppppo..", "..oppoppPo..", "..oppoppPo..", "..obbobbbo..", "..oooooooo.."];
const LEGS = ["..oppppppo..", "..oppoppPo..", "..oppoppPo..", "..obbobbbo..", "..oooooooo.."];
const LEGS_WALK = ["..oppppppo..", "..opp.oppo..", ".opp...oPPo.", ".obb...obbo.", ".ooo...ooo.."];

export type View = "front" | "back";
export type Pose = "sit" | "stand" | "walk" | "arms_up" | "carry";

export interface PersonOpts {
  view: View;
  pose: Pose;
  frame?: number; // walk frame 0/1
  carry?: RGB;
  /** a small game box tucked under the arm (people in the street who just bought yours) */
  tucked?: RGB;
}

export function studioPerson(lk: Look, o: PersonOpts): Bitmap {
  let rows = (o.view === "back" ? BACK : FRONT).map((r) => [...r]);
  const standing = o.pose !== "sit";
  if (o.view === "front" && !standing) rows = [...rows.slice(0, 12), [...".oooooooooo."]];
  if (o.view === "back" && standing) rows = [...rows.slice(0, -1), ...(o.pose === "walk" && o.frame ? LEGS_WALK : LEGS).map((r) => [...r])];
  if (o.view === "front" && o.pose === "walk" && o.frame) rows = [...rows.slice(0, 12), ...LEGS_WALK.map((r) => [...r])];
  if (lk.style === "long") {
    if (o.view === "back") for (const y of [5, 6, 7]) rows[y] = [..."..ohhhhhHo.."];
    else for (const y of [5, 6, 7]) {
      rows[y][2] = "h";
      rows[y][9] = "h";
    }
  }
  if (lk.style === "bun") rows = [[..."....oo......"], [..."...ohho....."], [..."....oo......"], ...rows];
  if (lk.style === "spiky") rows = [[..."..o.o.o.o..."], ...rows];
  if (lk.style === "cap") for (const y of [1, 2, 3]) rows[y] = rows[y].map((ch) => (ch === "h" ? "c" : ch));
  if (lk.style === "bald") for (const y of [1, 2]) rows[y] = rows[y].map((ch) => (ch === "h" ? "s" : ch));
  if (o.pose === "arms_up") {
    rows = [[..."o..........o"], [..."so........so"], [..."to........to"], ...rows];
    for (let y = 3; y < 9 && y < rows.length; y++) {
      rows[y][0] = "t";
      rows[y][11] = "t";
    }
  }
  if (lk.glasses && o.view === "front") {
    const ey = rows.findIndex((r) => r.includes("e"));
    if (ey >= 0) rows[ey] = rows[ey].map((ch, x) => (x >= 3 && x <= 8 && (ch === "s" || ch === "e") ? "g" : ch));
  }
  const cmap: Record<string, RGB> = {
    o: OUTLINE, h: lk.hair, H: shade(lk.hair, 0.7), s: lk.skin, S: shade(lk.skin, 0.82), e: OUTLINE, t: lk.shirt, T: shade(lk.shirt, 0.72),
    p: lk.pants, P: shade(lk.pants, 0.75), b: OUTLINE, c: lk.cap, g: [70, 60, 70],
  };
  const im = new Bitmap(12, rows.length);
  rows.forEach((r, y) => r.forEach((ch, x) => {
    const c = cmap[ch];
    if (c) im.set(x, y, c);
  }));
  if (o.pose === "carry" || o.carry) {
    const h = rows.length;
    im.box(2, h - 9, 9, h - 5, o.carry ?? [190, 146, 96], OUTLINE);
  }
  if (o.tucked) {
    // a flat box held at the hip: lid in the colour, a pale band where the title goes
    const h = rows.length;
    const x0 = o.view === "front" ? 7 : 1;
    im.box(x0, h - 10, x0 + 4, h - 6, o.tucked, OUTLINE);
    im.line(x0 + 1, h - 8, x0 + 3, h - 8, shade(o.tucked, 1.45));
    im.line(x0 + 1, h - 7, x0 + 3, h - 7, shade(o.tucked, 0.72));
  }
  return im;
}

// ------------------------------------------------------------------ 26 px close-up characters (from hd2d_preview.py)

export interface CloseupOpts {
  view: View;
  stand?: boolean;
  armsUp?: boolean;
  mouth?: "smile" | "grin" | "flat" | "open";
  glasses?: boolean;
}

export function closeupPerson(lk: Look, o: CloseupOpts): Bitmap {
  const w = 26;
  const h = o.stand ? 50 : 38;
  const im = new Bitmap(w, h);
  const hx = 13;
  const hy = 14;
  const hair = lk.hair;
  const skin = lk.skin;
  const shirt = lk.shirt;
  const hrD = shade(hair, 0.72);
  const hrL = shade(hair, 1.35);
  const skD = shade(skin, 0.84);
  const shD = shade(shirt, 0.72);
  const shL = shade(shirt, 1.2);
  const bot = o.stand ? 36 : h - 2;
  if (o.armsUp) for (const ax of [4, 22]) {
    im.box(ax - 1, 8, ax + 1, 21, shirt);
    im.box(ax - 1, 4, ax + 1, 7, skin);
  }
  // body
  im.box(6, 20, 20, bot, shirt);
  im.box(5, 22, 21, bot - 1, shirt);
  im.box(17, 22, 20, bot - 1, shD);
  im.line(8, 21, 13, 21, shL);
  if (o.stand) {
    im.box(8, 36, 18, 45, lk.pants);
    im.line(13, 38, 13, 45, shade(lk.pants, 0.7));
    im.box(7, 45, 12, 47, INK);
    im.box(14, 45, 19, 47, INK);
  }
  if (lk.style === "bun") {
    im.ellipse(hx - 3, hy - 13, hx + 3, hy - 7, hair);
    im.set(hx - 1, hy - 11, hrL);
  }
  if (lk.style === "spiky") im.poly([[hx - 7, hy - 3], [hx - 5, hy - 11], [hx - 2, hy - 6], [hx + 1, hy - 12], [hx + 3, hy - 6], [hx + 6, hy - 10], [hx + 7, hy - 2]], hair);
  if (lk.style !== "bald") im.ellipse(hx - 8, hy - 8, hx + 8, hy + 7, hair);
  else im.ellipse(hx - 7, hy - 7, hx + 7, hy + 7, skin);
  if (lk.style === "long") {
    im.box(hx - 8, hy, hx + 8, hy + 12, hair);
    im.box(hx + 5, hy + 2, hx + 8, hy + 12, hrD);
  }
  if (o.view === "front") {
    im.ellipse(hx - 6, hy - 3, hx + 6, hy + 7, skin);
    for (let y = hy - 3; y <= hy + 7; y++) for (let x = hx + 3; x <= hx + 6; x++) if (im.get(x, y)[0] === skin[0] && im.get(x, y)[3]) im.set(x, y, skD);
    if (lk.style !== "bald") for (let y = hy - 9; y <= hy - 3; y++) for (let x = hx - 8; x <= hx + 8; x++) {
      const dx = (x - hx) / 8.5;
      const dy = (y - (hy - 3)) / 6.5;
      if (dx * dx + dy * dy <= 1) im.set(x, y, hair);
    }
    im.box(hx - 3, hy + 1, hx - 3, hy + 2, INK);
    im.box(hx + 2, hy + 1, hx + 2, hy + 2, INK);
    for (const [px, py] of [[hx - 5, hy + 4], [hx - 4, hy + 4], [hx + 3, hy + 4], [hx + 4, hy + 4]]) im.set(px, py, [236, 132, 140]);
    const mouth = o.mouth ?? "smile";
    if (mouth === "grin") {
      im.box(hx - 2, hy + 4, hx + 2, hy + 5, INK);
      im.line(hx - 1, hy + 4, hx + 1, hy + 4, [250, 250, 250]);
    } else if (mouth === "open") im.box(hx - 1, hy + 4, hx + 1, hy + 6, INK);
    else if (mouth === "flat") im.line(hx - 2, hy + 5, hx + 1, hy + 5, INK);
    else {
      im.set(hx - 1, hy + 5, INK);
      im.set(hx, hy + 5, INK);
    }
    if (o.glasses ?? lk.glasses) {
      im.strokeBox(hx - 5, hy, hx - 1, hy + 3, INK);
      im.strokeBox(hx + 1, hy, hx + 5, hy + 3, INK);
    }
  } else {
    for (let y = hy - 8; y <= hy + 7; y++) for (let x = hx + 2; x <= hx + 8; x++) if (im.get(x, y)[3] && im.get(x, y)[0] === hair[0]) im.set(x, y, hrD);
    im.box(hx - 2, hy + 6, hx + 2, hy + 7, skD);
    im.box(hx - 9, hy, hx - 9, hy + 2, skin);
    im.box(hx + 9, hy, hx + 9, hy + 2, skD);
  }
  if (lk.style === "cap") {
    for (let y = hy - 10; y <= hy - 3; y++) for (let x = hx - 8; x <= hx + 8; x++) {
      const dx = (x - hx) / 8.5;
      const dy = (y - (hy - 3)) / 7.5;
      if (dx * dx + dy * dy <= 1) im.set(x, y, lk.cap);
    }
    im.line(hx - 6, hy - 6, hx - 2, hy - 8, shade(lk.cap, 1.4));
  }
  im.line(hx - 5, hy - 5, hx - 2, hy - 7, hrL);
  return im.outlined(INK);
}

/** A head-and-shoulders portrait (for staff cards and tester cards). */
export function portrait(lk: Look, mouth: CloseupOpts["mouth"] = "smile"): Bitmap {
  return closeupPerson(lk, { view: "front", mouth }).crop(0, 0, 28, 28);
}
